import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import PropTypes from "prop-types";
import React, { useLayoutEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";

import { AnimatePresence, motion } from "framer-motion";

import loadingBricksGif from "UI/images/loading_bricks.gif";

import "./FnrButton.scss";

FnrButton.propTypes = {
  children: PropTypes.any,
  onClick: PropTypes.func,
  text: PropTypes.string,
  iconBefore: PropTypes.object,
  iconAfter: PropTypes.object,
  disabled: PropTypes.bool,
  loading: PropTypes.bool,
  link: PropTypes.string,
  color: PropTypes.string,
  size: PropTypes.string,
  className: PropTypes.string,
  hide: PropTypes.bool,
};

export default function FnrButton({
  children,
  onClick,
  text,
  iconBefore,
  iconAfter,
  link,
  disabled,
  loading,
  size = "medium",
  color = "gray",
  className = "",
  hide = false,
  ...rest
}) {
  const [shouldHover, setShouldHover] = useState(false);
  const [hovering, setHovering] = useState(false);
  const [hoverAnimations, setHoverAnimation] = useState(false);
  const [buttonDimensions, setButtonDimensions] = useState({});

  const buttonRef = useRef();
  const pointerPosition = useRef({});
  const setPointerPosition = (newPosition) => {
    pointerPosition.current = newPosition;
  };
  const updatePointerPositionFromMouseEvent = (evt) => {
    setPointerPosition({ x: evt.clientX, y: evt.clientY });
  };

  const onHoverStart = (event, info) => {
    setPointerPosition(info.point);
    setShouldHover(true);
  };

  const onHoverEnd = (event, info) => {
    setPointerPosition(info.point);
    setShouldHover(false);
  };

  const getPointerOffset = (keepInBounds = false) => {
    const newButtonDimensions = buttonRef.current.getBoundingClientRect();

    const offset = {
      x: pointerPosition.current.x - buttonDimensions.left,
      y: pointerPosition.current.y - buttonDimensions.top,
    };

    if (keepInBounds) {
      if (offset.x < 0) offset.x = 0;
      else if (offset.x > buttonDimensions.width)
        offset.x = buttonDimensions.width;
      if (offset.y < 0) offset.y = 0;
      else if (offset.y > buttonDimensions.height)
        offset.y = buttonDimensions.height;
    }

    offset.x -= 4;
    offset.y -= 4;

    setButtonDimensions(newButtonDimensions);

    return offset;
  };

  const getAnimation = () => {
    const offset = getPointerOffset(true);

    const animations = {
      style: {
        opacity: 0,
        width: "8px",
        height: "8px",
        transform: `translate(${offset.x}px, ${offset.y}px)`,
      },
      initial: {
        opacity: 0,
        width: 8,
        height: 8,
        x: offset.x,
        y: offset.y,
      },
      animate: {
        opacity: 1,
        width: "calc(100% - 6px)",
        height: "calc(100% - 6px)",
        x: 3,
        y: 3,
      },
      exit: {
        opacity: 0,
        width: 8,
        height: 8,
        x: offset.x,
        y: offset.y,
      },
    };

    return animations;
  };

  useLayoutEffect(() => {
    if (shouldHover && !hovering) {
      setHoverAnimation(getAnimation());
      setHovering(true);
    } else if (!shouldHover && hovering) {
      setHoverAnimation((currentAnimation) => {
        const offset = getPointerOffset(true);

        currentAnimation.exit.x = offset.x;
        currentAnimation.exit.y = offset.y;

        return currentAnimation;
      });
      setHovering(false);
    }

    // Aug 2021 → we’re doing a clean slate of all warnings and ain’t got time to look at this so that’s why this is disabled
    // eslint-disable-next-line
  }, [shouldHover, hovering]);

  useLayoutEffect(() => {
    setButtonDimensions(buttonRef.current.getBoundingClientRect());
  }, []);

  const loadingIndicatorAnimations = {
    initial: { opacity: 0, scale: 0.5 },
    animate: { opacity: 1, scale: 1 },
    exit: { opacity: 0, scale: 0.5 },
  };

  const tapScale = {
    small: 0.92,
    medium: 0.94,
  };

  if (hide) return null;

  return (
    <motion.button
      className={"FnrButton color_" + color + " " + size + " " + className}
      onClick={onClick}
      ref={buttonRef}
      disabled={disabled}
      onMouseMove={updatePointerPositionFromMouseEvent}
      onHoverStart={onHoverStart}
      onHoverEnd={onHoverEnd}
      {...rest}
    >
      <AnimatePresence>
        {hovering && !loading && !disabled && hoverAnimations && (
          <motion.div key="hover" className="hover" {...hoverAnimations} />
        )}
      </AnimatePresence>
      <motion.div
        className="loading_container"
        animate={{
          opacity: loading ? 0 : disabled ? 0.6 : 1,
          scale: loading ? 0.5 : 1,
        }}
        whileTap={{
          scale: disabled ? 1 : tapScale[size] || 0.96,
        }}
      >
        {link && !disabled ? (
          <Link to={link}>
            <FnrButtonInside
              iconBefore={iconBefore}
              text={text}
              // Aug 2021 → we’re doing a clean slate of all warnings and ain’t got time to look at this so that’s why this is disabled
              // eslint-disable-next-line
              children={children}
              iconAfter={iconAfter}
            />
          </Link>
        ) : (
          <FnrButtonInside
            iconBefore={iconBefore}
            text={text}
            // Aug 2021 → we’re doing a clean slate of all warnings and ain’t got time to look at this so that’s why this is disabled
            // eslint-disable-next-line
            children={children}
            iconAfter={iconAfter}
          />
        )}
      </motion.div>
      <AnimatePresence>
        {loading && (
          <motion.div
            className="loading_indicator"
            {...loadingIndicatorAnimations}
          >
            <img src={loadingBricksGif} alt="" />
          </motion.div>
        )}
      </AnimatePresence>
    </motion.button>
  );
}

function FnrButtonInside({ iconBefore, text, children, iconAfter }) {
  return (
    <motion.div className="inside" whileTap={{ scale: 0.95 }}>
      {iconBefore && (
        <div className="icon">
          <FontAwesomeIcon icon={iconBefore} />
        </div>
      )}
      {text && <div className="text">{text}</div>}
      {children}
      {iconAfter && (
        <div className="icon">
          <FontAwesomeIcon icon={iconAfter} />
        </div>
      )}
    </motion.div>
  );
}
